"use client";

import { useState } from "react";
import {
  MoreHorizontal,
  Edit,
  Trash2,
  Star,
  MapPin,
  Filter,
  Download,
  Plus,
  Pause,
  Power,
} from "lucide-react";
import { Button } from "../UI/Button";
import { Input } from "../UI/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../UI/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../UI/dropdown-menu";
import { Badge } from "../UI/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../UI/select";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { myHeaders } from "../../config/axios";
import { URL_API } from "../../config/default";
import { Link } from "react-router-dom";
import { QualyProms } from "../../utils/qualy-proms";
import { CustomToast } from "../../utils/custom-toast";
import Spinner from "../UI/Spinner/Spinner";
import Pagination from "../../utils/pagination";

export default function AccommodationsTable() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [filter, setFilter] = useState<any>({
    status: "activo",
    search: "",
    type: "",
  });

  const espacios = [
    {
      name: "Todos",
    },
    {
      name: "Casa",
    },
    {
      name: "Departamento",
    },
    {
      name: "Hotel",
    },
    {
      name: "Cabaña",
    },
    {
      name: "Excursión",
    },
  ];

  const buscar = (value: string) => {
    setFilter({
      ...filter,
      search: value,
    });
    setSearchTerm(value);
  };

  const status = (status: any, id: any) => {
    {
      let formdata = new FormData();

      formdata.append("status", status);

      const requestOptions = {
        method: "POST",
        body: formdata,
        headers: {
          Authorization: myHeaders, // Un ejemplo de header de autenticación
        },
      };

      fetch(`${URL_API}/properties/${id}`, requestOptions)
        .then(async () => {
          //const property = await response.json();
          CustomToast({
            status: 200,
            msg: "",
            title: "Alojamiento editado con exito",
          });
          queryClient.invalidateQueries({
            queryKey: ["misAlojamientos", filter],
          });
        })
        .then((result) => console.log(result))
        .catch((error) => console.error(error));
    }
  };

  const { isPending, error, data } = useQuery({
    queryKey: ["misAlojamientos", filter],
    queryFn: () => {
      const authorization = new Headers();
      authorization.append("Authorization", myHeaders);
      const params = new URLSearchParams(filter);

      return fetch(`${URL_API}/properties?${params}`, {
        method: "GET",
        headers: {
          Authorization: myHeaders,
        },
      }).then((res) => res.json());
    },
  });

  if (isPending) {
    return (
      <div className="flex justify-center items-center py-5 text-black">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return "";
  }

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "activo":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            Activo
          </Badge>
        );
      case "inactivo":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            Inactivo
          </Badge>
        );
      case "pendiente":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            Pendiente
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 sm:p-6">
        <div className="flex-1">
          <Input
            placeholder="Buscar alojamientos..."
            value={searchTerm}
            onChange={(e) => buscar(e.target.value)}
            className="max-w-sm"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setFilter({
                ...filter,
                status: value,
              });
              setStatusFilter(value);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="activo">Activo</SelectItem>
              <SelectItem value="inactivo">Inactivo</SelectItem>
              <SelectItem value="pendiente">Pendiente</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={typeFilter}
            onValueChange={(value) => {
              {
                setFilter({
                  ...filter,
                  type: value,
                });
                setTypeFilter(value);
              }
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              {espacios.map((espacio) => {
                return (
                  <SelectItem value={espacio.name}>{espacio.name}</SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon">
            <Filter className="w-4 h-4" />
          </Button>

          <Button variant="outline" size="icon">
            <Download className="w-4 h-4" />
          </Button>

          <Link
            to={"/alquiler/carga"}
            className={`cursor-pointer flex rounded-full btn btn-filled-2`}
            style={{ borderRadius: "20px" }}>
            <span className="flex justify-center items-center">
              <Plus className="w-[20px] me-2" />
              Nuevo Alojamiento</span>
          </Link>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">Alojamiento</TableHead>
                <TableHead>Ubicación</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Precio/Noche</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Capacidad</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Propietario</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data.map((accommodation: any) => (
                <TableRow key={accommodation.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="relative w-16 h-12 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={
                            accommodation.images.length > 0
                              ? accommodation.images[0].url
                              : "./src/assets/img/logo-en-blanco.png"
                          }
                          alt={accommodation.title}
                          className="object-cover"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {accommodation.title}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {accommodation.id}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin className="w-3 h-3" />
                      {accommodation.city}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{accommodation.type}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">${accommodation.price}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-medium">
                        {QualyProms(accommodation.review)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>{accommodation.travellers} huéspedes</div>
                      <div className="text-gray-500 text-nowrap">
                        {accommodation.bedrooms} habitaciones
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(accommodation.status)}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">{""}</div>
                      <div className="text-gray-500">
                        {accommodation.created_at}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Abrir menú</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                        <DropdownMenuItem>
                          <Link
                            className="text-nowrap flex"
                            to={`/alquiler/editar/${accommodation.slug}`}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Editar
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-black">
                          <label className="cursor-pointer text-npwrap flex">
                            <Power className="mr-2 h-4 w-4" />
                            Activar
                            <button
                              className="hidden"
                              onClick={() => status("activo", accommodation.id)}
                            ></button>
                          </label>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-black">
                          <label className="cursor-pointer text-npwrap flex">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Eliminar
                            <button
                              className="hidden"
                              onClick={() =>
                                status("eliminado", accommodation.id)
                              }
                            ></button>
                          </label>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-black">
                          <label className="cursor-pointer text-npwrap flex">
                            <Pause className="mr-2 h-4 w-4" />
                            Pausar
                            <button
                              className="hidden"
                              onClick={() =>
                                status("inactivo", accommodation.id)
                              }
                            ></button>
                          </label>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination data={data} setPage={setFilter} page={filter} />
    </div>
  );
}
